// src/components/judge/JudgeEventSetup.jsx
import React, { useState, useEffect } from 'react';
import { Settings, Plus, X, Trash2, CheckCircle, Save, AlertTriangle, Info } from 'lucide-react';
import { judgeConfigService } from '../../services/supabaseService';
import { getStandardTemplate, getTemplatesForDiscipline, calculateTotal } from '../../utils/scoringTemplates';
import { getApparatusForEvent } from '../../utils/disciplineUtils';
import './JudgeEventSetup.css';

const TYPE_COLORS = {
  difficulty: { bg: '#f3e8ff', color: '#7c3aed', label: 'Dificultad' },
  execution:  { bg: '#dbeafe', color: '#2563eb', label: 'Ejecución' },
  artistic:   { bg: '#fce7f3', color: '#db2777', label: 'Artístico' },
  penalty:    { bg: '#fee2e2', color: '#dc2626', label: 'Penalización' },
  bonus:      { bg: '#d1fae5', color: '#059669', label: 'Bonificación' },
  score:      { bg: '#e0f2fe', color: '#0284c7', label: 'Puntaje' },
  deduction:  { bg: '#fee2e2', color: '#dc2626', label: 'Deducción' },
  time:       { bg: '#fef3c7', color: '#d97706', label: 'Tiempo' },
  time_of_flight: { bg: '#fef3c7', color: '#d97706', label: 'T. Vuelo' },
  h_displacement: { bg: '#ffedd5', color: '#ea580c', label: 'Desplazamiento' },
};

// Roles de juez disponibles para selección
const JUDGE_ROLE_OPTIONS = [
  { code: 'standard', label: 'Juez Estándar', icon: '⚫', description: 'Evalúa todas las componentes: Dificultad + Ejecución - Penalizaciones. Ideal para eventos con pocos jueces.', color: '#8b5cf6' },
  { code: 'D', label: 'Panel D — Dificultad', icon: '🟣', description: 'Evalúa únicamente el valor de dificultad de la rutina.', color: '#7c3aed' },
  { code: 'E', label: 'Panel E — Ejecución', icon: '🔵', description: 'Evalúa la calidad técnica de ejecución (parte de 10.0).', color: '#2563eb' },
  { code: 'A', label: 'Panel A — Artístico', icon: '🩷', description: 'Evalúa el componente artístico y presentación.', color: '#db2777' },
  { code: 'L', label: 'Juez de Línea', icon: '🟢', description: 'Evalúa salidas de área e infracciones de línea.', color: '#059669' },
  { code: 'T', label: 'Juez de Tiempo', icon: '🟡', description: 'Controla el tiempo de la rutina.', color: '#d97706' },
];

/**
 * Filtra las columnas de la plantilla según el rol del juez
 */
function getColumnsForRole(template, judgeRole) {
  if (!template || !template.columns) return [];
  if (judgeRole === 'standard') return template.columns; // All columns
  
  // Map role to column types
  const roleColumnMap = {
    'D': ['difficulty'],
    'E': ['execution'],
    'A': ['artistic'],
    'L': ['penalty'],  // Line judges evaluate line penalties
    'T': ['time'],     // Time judges
  };
  
  const allowedTypes = roleColumnMap[judgeRole];
  if (!allowedTypes) return template.columns;
  
  return template.columns.filter(col => 
    allowedTypes.includes(col.type) || col.panel === judgeRole
  );
}

const JudgeEventSetup = ({ event, judgeId, onConfigured, onClose, isInitialSetup = false }) => {
  // Steps: role → apparatus → template
  const [judgeRole, setJudgeRole] = useState(null); // 'standard', 'D', 'E', 'A', 'L', 'T'
  const [selectedApparatus, setSelectedApparatus] = useState(null);
  const [templateType, setTemplateType] = useState('standard'); // 'standard' | 'custom'
  const [selectedTemplateKey, setSelectedTemplateKey] = useState(null);
  const [customColumns, setCustomColumns] = useState([
    { key: 'col_1', label: 'Dificultad', type: 'difficulty', max_value: null, panel: null },
    { key: 'col_2', label: 'Ejecución', type: 'execution', max_value: 10, panel: null },
    { key: 'col_3', label: 'Penalización', type: 'penalty', max_value: null, panel: null },
  ]);
  const [existingConfigs, setExistingConfigs] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const availableApparatus = getApparatusForEvent(event);
  const standardTemplate = getStandardTemplate(event.discipline, event.category);
  const availableTemplates = getTemplatesForDiscipline(event.discipline);

  // Determine which roles make sense for this discipline
  const disciplineHasArtistic = standardTemplate.columns?.some(c => c.type === 'artistic');
  const disciplineHasTime = standardTemplate.columns?.some(c => c.type === 'time');
  const visibleRoles = JUDGE_ROLE_OPTIONS.filter(r => {
    if (r.code === 'standard') return true;
    if (r.code === 'D' || r.code === 'E') return true; // Always show D and E
    if (r.code === 'A') return disciplineHasArtistic;
    if (r.code === 'T') return disciplineHasTime;
    if (r.code === 'L') return true;
    return true;
  });

  // Load existing configurations
  useEffect(() => {
    const loadConfigs = async () => {
      try {
        const { data } = await judgeConfigService.getConfigsByEvent(judgeId, event.id);
        const configMap = {};
        (data || []).forEach(c => {
          configMap[c.apparatus] = c;
        });
        setExistingConfigs(configMap);
      } catch (err) {
        console.error('Error loading configs:', err);
      } finally {
        setLoading(false);
      }
    };
    loadConfigs();
  }, [judgeId, event.id]);

  // Set default template key
  useEffect(() => {
    if (standardTemplate && !selectedTemplateKey) {
      setSelectedTemplateKey(standardTemplate.key);
    }
  }, [standardTemplate, selectedTemplateKey]);

  // When selecting an apparatus, load its existing config
  const handleSelectApparatus = (appCode) => {
    setSelectedApparatus(appCode);
    const existing = existingConfigs[appCode];
    if (existing) {
      setTemplateType(existing.template_type);
      if (existing.judge_role) setJudgeRole(existing.judge_role);
      if (existing.template_type === 'custom') {
        setCustomColumns(existing.columns || []);
      } else if (existing.template_key) {
        setSelectedTemplateKey(existing.template_key);
      }
    } else {
      setTemplateType('standard');
      setSelectedTemplateKey(standardTemplate.key);
    }
  };

  // Get the currently active template based on selection
  const getActiveTemplate = () => {
    if (templateType === 'standard' && selectedTemplateKey) {
      const allTemplates = getTemplatesForDiscipline(event.discipline);
      return allTemplates.find(t => t.key === selectedTemplateKey) || standardTemplate;
    }
    return standardTemplate;
  };

  // Get columns based on role
  const getActiveColumns = () => {
    if (templateType === 'custom') {
      return customColumns.filter(c => c.label.trim() !== '');
    }
    const tpl = getActiveTemplate();
    return getColumnsForRole(tpl, judgeRole);
  };

  // Etiquetas por defecto según el tipo de columna
  const TYPE_DEFAULT_LABELS = {
    difficulty: 'Dificultad',
    execution: 'Ejecución',
    artistic: 'Artístico',
    score: 'Puntaje',
    bonus: 'Bonificación',
    penalty: 'Penalización',
    deduction: 'Deducción',
    time: 'Tiempo',
  };

  // Todos los labels por defecto para saber si el usuario NO ha personalizado el nombre
  const allDefaultLabels = Object.values(TYPE_DEFAULT_LABELS);

  // Custom column management
  const handleAddColumn = () => {
    setCustomColumns(prev => [...prev, {
      key: `col_${Date.now()}`,
      label: 'Puntaje',
      type: 'score',
      max_value: 10,
      panel: null
    }]);
  };

  const handleRemoveColumn = (index) => {
    if (customColumns.length <= 1) return;
    setCustomColumns(prev => prev.filter((_, i) => i !== index));
  };

  const handleColumnChange = (index, field, value) => {
    setCustomColumns(prev => prev.map((col, i) => {
      if (i !== index) return col;
      const updated = { ...col, [field]: field === 'max_value' ? (value === '' ? null : Number(value)) : value };
      // Si cambia el tipo y el label es un valor por defecto, auto-actualizarlo
      if (field === 'type' && allDefaultLabels.includes(col.label)) {
        updated.label = TYPE_DEFAULT_LABELS[value] || col.label;
      }
      return updated;
    }));
  };

  // Save configuration
  const handleSave = async () => {
    if (!selectedApparatus || !judgeRole) return;

    const columns = getActiveColumns();
    if (columns.length === 0) return;

    setSaving(true);
    try {
      const { data, error } = await judgeConfigService.saveConfig(
        judgeId,
        event.id,
        selectedApparatus,
        templateType,
        columns,
        judgeRole
      );

      if (error) {
        console.error('Error saving config:', error);
      }

      // Actualizar configs locales (usar columnas locales como fallback)
      const savedColumns = (data && data.columns) ? data.columns : columns;
      setExistingConfigs(prev => ({
        ...prev,
        [selectedApparatus]: {
          template_type: templateType,
          columns: savedColumns,
          judge_role: judgeRole,
          ...(data || {})
        }
      }));

      // Siempre notificar al padre con las columnas editadas para actualizar la tabla
      if (onConfigured) {
        onConfigured(selectedApparatus, savedColumns, templateType);
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleUseExistingConfig = (appCode) => {
    const config = existingConfigs[appCode];
    if (config && onConfigured) {
      onConfigured(appCode, config.columns, config.template_type);
    }
  };

  const activeTemplate = getActiveTemplate();
  const activeColumns = getActiveColumns();

  const canSave = (() => {
    if (!selectedApparatus || !judgeRole) return false;
    if (templateType === 'custom') {
      // At least one column with a label
      return customColumns.some(c => c.label && c.label.trim() !== '');
    }
    // standard: check columns exist for this role (use getActiveColumns inline)
    const cols = getActiveColumns();
    return cols.length > 0;
  })();

  if (loading) {
    return (
      <div className="jes-overlay" onClick={onClose}>
        <div className="jes-modal" onClick={e => e.stopPropagation()}>
          <div className="jes-body" style={{ textAlign: 'center', padding: '40px' }}>
            <div className="spinner" /><p>Cargando configuraciones...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="jes-overlay" onClick={isInitialSetup ? undefined : onClose}>
      <div className="jes-modal jes-modal-wide" onClick={e => e.stopPropagation()}>
        <div className="jes-header">
          <h2><Settings size={20} /> Configurar mi Evaluación</h2>
          <p>{event.name} — {activeTemplate.name}</p>
          {isInitialSetup && (
            <p className="jes-initial-hint">⚡ Configura al menos un aparato antes de empezar a evaluar</p>
          )}
        </div>

        <div className="jes-body">
          {/* Formula info banner */}
          <div className="jes-formula-banner">
            <div className="jes-formula-icon">📐</div>
            <div className="jes-formula-content">
              <strong>Fórmula FIG: {activeTemplate.formula}</strong>
              <span>{activeTemplate.description}</span>
            </div>
          </div>

          {/* ====== STEP 1: Judge Role ====== */}
          <div className="jes-step">
            <div className="jes-step-label"><span className="jes-step-num">1</span> ¿Qué tipo de juez serás?</div>
            <div className="jes-role-grid">
              {visibleRoles.map(role => {
                const isSelected = judgeRole === role.code;
                return (
                  <div
                    key={role.code}
                    className={`jes-role-card ${isSelected ? 'active' : ''}`}
                    onClick={() => setJudgeRole(role.code)}
                    style={{ '--role-color': role.color }}
                  >
                    <div className="jes-role-radio" />
                    <div className="jes-role-info">
                      <div className="jes-role-title">
                        <span className="jes-role-icon">{role.icon}</span>
                        {role.label}
                      </div>
                      <p>{role.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ====== STEP 2: Apparatus ====== */}
          {judgeRole && (
            <div className="jes-step">
              <div className="jes-step-label"><span className="jes-step-num">2</span> Selecciona el aparato</div>
              <div className="jes-apparatus-grid">
                {availableApparatus.map(app => {
                  const isConfigured = !!existingConfigs[app.code];
                  const isSelected = selectedApparatus === app.code;
                  return (
                    <button
                      key={app.code}
                      className={`jes-apparatus-btn ${isSelected ? 'active' : ''} ${isConfigured ? 'configured' : ''}`}
                      onClick={() => handleSelectApparatus(app.code)}
                    >
                      {isConfigured && <CheckCircle size={14} />}
                      {app.name}
                    </button>
                  );
                })}
              </div>

              {selectedApparatus && existingConfigs[selectedApparatus] && (
                <div className="jes-existing-badge">
                  <CheckCircle size={14} />
                  Ya tienes una configuración para este aparato.
                  <button className="jes-use-existing-btn" onClick={() => handleUseExistingConfig(selectedApparatus)}>
                    Usar y continuar
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ====== STEP 3: Template ====== */}
          {judgeRole && selectedApparatus && (
            <div className="jes-step">
              <div className="jes-step-label"><span className="jes-step-num">3</span> Plantilla de puntuación</div>

              <div className="jes-template-options">
                <div
                  className={`jes-template-card ${templateType === 'standard' ? 'active' : ''}`}
                  onClick={() => setTemplateType('standard')}
                >
                  <div className="jes-template-radio" />
                  <div className="jes-template-info">
                    <h4>📐 Plantilla Estándar FIG</h4>
                    <p>Basada en el Código de Puntuación FIG 2024-2028 para {activeTemplate.name}.
                      {judgeRole !== 'standard' && ` Solo verás las columnas de tu panel (${judgeRole}).`}
                    </p>
                  </div>
                </div>

                <div
                  className={`jes-template-card ${templateType === 'custom' ? 'active' : ''}`}
                  onClick={() => setTemplateType('custom')}
                >
                  <div className="jes-template-radio" />
                  <div className="jes-template-info">
                    <h4>✏️ Plantilla Personalizada</h4>
                    <p>Define tus propias columnas de evaluación con criterios personalizados.</p>
                  </div>
                </div>
              </div>

              {/* Standard template preview */}
              {templateType === 'standard' && (
                <div className="jes-standard-preview">
                  {availableTemplates.length > 1 && (
                    <div className="jes-template-selector">
                      <label>Variante:</label>
                      <select value={selectedTemplateKey || ''} onChange={e => setSelectedTemplateKey(e.target.value)}>
                        {availableTemplates.map(t => (
                          <option key={t.key} value={t.key}>{t.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Role-specific info */}
                  {judgeRole !== 'standard' && (
                    <div className="jes-role-filter-info">
                      <Info size={15} />
                      <span>Como <strong>juez {JUDGE_ROLE_OPTIONS.find(r => r.code === judgeRole)?.label}</strong>, solo verás las columnas correspondientes a tu panel. El sistema combinará tu puntuación con la de los demás jueces.</span>
                    </div>
                  )}

                  {/* Panels overview (only for standard role) */}
                  {judgeRole === 'standard' && activeTemplate.panels && Object.keys(activeTemplate.panels).length > 0 && (
                    <div className="jes-panels-info">
                      <h5>📊 Paneles de evaluación</h5>
                      <div className="jes-panels-grid">
                        {Object.entries(activeTemplate.panels).map(([key, panel]) => (
                          <div key={key} className={`jes-panel-card jes-panel-${key}`}>
                            <div className="jes-panel-letter">{key}</div>
                            <div className="jes-panel-details">
                              <strong>{panel.label}</strong>
                              <span>{panel.judges} juez(ces) • {
                                panel.method === 'trimmed_mean' ? 'Media recortada' :
                                panel.method === 'consensus' ? 'Consenso' :
                                panel.method === 'average' ? 'Promedio' : 'Individual'
                              }</span>
                              {panel.startScore && <span className="jes-panel-start">Parte de {panel.startScore}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Columns preview — filtered by role */}
                  <div className="jes-columns-preview-section">
                    <h5>📋 Columnas que verás al evaluar</h5>
                    <div className="jes-columns-preview-list">
                      {activeColumns.map(col => {
                        const typeInfo = TYPE_COLORS[col.type] || TYPE_COLORS.score;
                        return (
                          <div key={col.key} className="jes-col-preview-item">
                            <span className="jes-col-preview-badge" style={{ background: typeInfo.bg, color: typeInfo.color }}>
                              {typeInfo.label}
                            </span>
                            <span className="jes-col-preview-label">{col.label}</span>
                            {col.max_value && <span className="jes-col-preview-max"></span>}
                            {col.startScore && <span className="jes-col-preview-start"></span>}
                          </div>
                        );
                      })}
                      {activeColumns.length === 0 && (
                        <div className="jes-col-preview-empty">
                          <AlertTriangle size={16} />
                          No hay columnas para este rol en la plantilla actual. Usa una plantilla personalizada.
                        </div>
                      )}
                      {activeColumns.length > 0 && (
                        <div className="jes-col-preview-item jes-col-preview-total">
                          <span className="jes-col-preview-badge" style={{ background: '#d1fae5', color: '#065f46' }}>= Total</span>
                          <span className="jes-col-preview-label">Calculado automáticamente</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {judgeRole === 'standard' && Object.values(activeTemplate.panels || {}).some(p => p.method === 'trimmed_mean') && (
                    <div className="jes-trim-info">
                      <AlertTriangle size={16} />
                      <span>Este sistema usa <strong>eliminación de extremos</strong>: se quita la nota más alta y más baja del Panel E antes de promediar. Aplica cuando hay 3+ jueces en el mismo panel.</span>
                    </div>
                  )}
                </div>
              )}

              {/* Custom columns editor */}
              {templateType === 'custom' && (
                <div className="jes-custom-editor">
                  <div className="jes-custom-header">
                    <span>Define las columnas de evaluación</span>
                  </div>
                  {customColumns.map((col, index) => (
                    <div key={col.key} className="jes-custom-col">
                      <span className="jes-custom-col-num">{index + 1}</span>
                      <input
                        type="text"
                        placeholder="Nombre del criterio"
                        value={col.label}
                        onChange={e => handleColumnChange(index, 'label', e.target.value)}
                        className="jes-custom-input"
                      />
                      <select
                        value={col.type}
                        onChange={e => handleColumnChange(index, 'type', e.target.value)}
                        className="jes-custom-select"
                      >
                        <option value="difficulty">Dificultad (+)</option>
                        <option value="execution">Ejecución (+)</option>
                        <option value="artistic">Artístico (+)</option>
                        <option value="score">Puntaje (+)</option>
                        <option value="bonus">Bonificación (+)</option>
                        <option value="penalty">Penalización (−)</option>
                        <option value="deduction">Deducción (−)</option>
                        <option value="time">Tiempo</option>
                      </select>
                      <input
                        type="number"
                        placeholder="Máx"
                        value={col.max_value ?? ''}
                        onChange={e => handleColumnChange(index, 'max_value', e.target.value)}
                        className="jes-custom-max"
                        min="0"
                        step="0.5"
                      />
                      <button
                        className="jes-custom-col-remove"
                        onClick={() => handleRemoveColumn(index)}
                        disabled={customColumns.length <= 1}
                        title="Eliminar columna"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                  <button className="jes-add-col-btn" onClick={handleAddColumn}>
                    <Plus size={16} /> Agregar columna
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="jes-actions">
            {!isInitialSetup && (
              <button className="jes-btn jes-btn-ghost" onClick={onClose}>
                <X size={16} /> Cancelar
              </button>
            )}
            <button
              className="jes-btn jes-btn-primary"
              onClick={handleSave}
              disabled={!canSave || saving}
            >
              {saving ? '⏳ Guardando...' : <><Save size={16} /> Guardar y Evaluar</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JudgeEventSetup;
